import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, mutate } from '../shared/api';
import { formatMoney } from '../shared/formatting';
import { DateInput } from '../shared/DateInput';
import { PnlKockaSettings } from './pnlKockaSettings';
import { PnlV2Module, periodRange, type PnlV2Payload, type ComparisonMode, type CustomComparePeriod, type ExplicitCompare } from './pnlV2Module';

/**
 * m.2.3 P&L nad kockom (bi_pl_facts_v2): izgled P&L V2, podaci iz /api/pl-v23.
 * Nacini: knjigovodstveno (knjig), kontrolno knjigovodstveno (sim_knjig), ekonomski (ekon).
 * Slojevi: drustvo (zadano; konsolidacija = vise drustava u izborniku TVRTKE + Konsolidirano), sektor, MT, regija, partner.
 * Klik na redak sloja filtrira cijeli pregled na tu vrijednost; drill ide kategorija -> podkategorija -> partner -> dokument.
 */
type Mode = 'knjig' | 'sim_knjig' | 'ekon';
type LayerRow = { key: string; label: string; revenue: number; cogs: number; gross: number; opex: number; ebitda: number; net: number; count: number; negative_gross: boolean; alloc_warning?: string | null };
type KockaPayload = PnlV2Payload & {
    layer?: { key: string; label: string; rows: LayerRow[] };
    layers?: Record<string, string>;
    modes?: Record<Mode, string>;
    built_at?: string | null;
    allocation?: { available: boolean; applied: boolean; note: string | null; unallocated: Record<string, number>; overridden: Record<string, string[]> };
    cache?: { hit: boolean; computed_at: string; cube_built_at: string | null };
    sub_buckets?: Array<{ bucket_lvl1: string; bucket_lvl2: string; total_eur: number; count: number }>;
};
type SortKey = 'label' | 'revenue' | 'cogs' | 'gross' | 'opex' | 'ebitda' | 'net';
const pct = (a: number, b: number) => (Math.abs(b) >= 0.01 ? `${((a / Math.abs(b)) * 100).toLocaleString('hr-HR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %` : '-');

const MODES: Array<{ key: Mode; label: string }> = [
    { key: 'knjig', label: 'Knjigovodstveno' },
    { key: 'sim_knjig', label: 'Kontrolno knjigovodstveno' },
    { key: 'ekon', label: 'Ekonomski' },
];
const LAYERS: Array<{ key: string; label: string }> = [
    { key: 'drustvo', label: 'Drustvo' },
    { key: 'sektor', label: 'Sektor' },
    { key: 'mt', label: 'Mjesto troska' },
    { key: 'regija', label: 'Regija' },
    { key: 'partner', label: 'Partner' },
    { key: 'kanal', label: 'Kanal' },
    { key: 'grupa', label: 'Grupa poslovnica' },
    { key: 'metal', label: 'Metal' },
];
const lbl: React.CSSProperties = { display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.35rem', color: '#9c8a55' };

/**
 * Zadani raspon: 1.1. godine zadnjeg zatvorenog mjeseca do kraja prethodnog mjeseca.
 * Tekuci mjesec nije zatvoren (knjizenja kasne), pa ne ulazi u default. Korisnik raspon mijenja u zaglavlju.
 */
function defaultRange(): { from: string; to: string } {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    return { from: `${end.getFullYear()}-01-01`, to: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}` };
}

export function PnlKockaModule({ token, locale = 'hr', companyIds, companies = [], onFiltersChange, onExit }: {
    token: string | null;
    locale?: 'hr' | 'en';
    companyIds: number[];
    /** Popis tvrtki za izbor u zaglavlju (fullscreen skriva globalni filtar). */
    companies?: Array<{ id: number; code: string }>;
    /** Izbor tvrtki ide u globalni filtar. Raspon je lokalan (vidi defaultRange). */
    onFiltersChange?: (patch: { companyIds?: number[] }) => void;
    onExit?: () => void;
}) {
    const [range, setRange] = useState(defaultRange);
    const [view, setView] = useState<'pnl' | 'postavke'>('pnl');
    const dateFrom = range.from;
    const dateTo = range.to;
    const [mode, setMode] = useState<Mode>('ekon');
    const [layer, setLayer] = useState('drustvo');
    const [layerValue, setLayerValue] = useState<{ key: string; label: string } | null>(null);
    const [icMode, setIcMode] = useState<'include' | 'eliminate' | 'isolate'>('include');
    const [cmpMode, setCmpMode] = useState<ComparisonMode>('none');
    const [customCmp, setCustomCmp] = useState<CustomComparePeriod>({ date_from: '2025-01-01', date_to: '2025-12-31', label: 'custom' });
    const [explicitCmp, setExplicitCmp] = useState<ExplicitCompare>({ current_key: '2026-Q3', compare_key: '2026-Q2' });
    const [payload, setPayload] = useState<KockaPayload | null>(null);
    const [cmpPayload, setCmpPayload] = useState<KockaPayload | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [alloc, setAlloc] = useState(true);
    const [refreshTick, setRefreshTick] = useState(0);
    const lastTick = useRef(0);
    const [rebuilding, setRebuilding] = useState(false);
    // Vedran 30.09.: kao 2.4 - klik na redak sloja otvara drawer (filtar je gumb u retku i u draweru), klik na zaglavlje sortira
    const [sort, setSort] = useState<{ col: SortKey; dir: 1 | -1 } | null>(null);
    const [drawer, setDrawer] = useState<LayerRow | null>(null);
    const [drawerData, setDrawerData] = useState<KockaPayload | null>(null);
    const [drawerErr, setDrawerErr] = useState('');

    // Crveni gumb: podigni runtime verziju cachea na serveru (svi filtri postaju promaseni) pa ponovno ucitaj.
    const rebuildCache = () => {
        if (!token || rebuilding) return;
        setRebuilding(true);
        mutate('/pl-v23/cache/rebuild', token, 'POST')
            .catch(() => {})
            .finally(() => { setRebuilding(false); setRefreshTick((t) => t + 1); });
    };

    const extra = useMemo(() => {
        const e: Record<string, string> = { mode, layer, intercompany: icMode, alloc: alloc ? '1' : '0' };
        if (layerValue) e.layer_value = layerValue.key;
        return e;
    }, [mode, layer, layerValue, icMode, alloc]);

    let mainFrom = dateFrom;
    let mainTo = dateTo;
    if (cmpMode === 'mom' || cmpMode === 'qoq' || cmpMode === 'yoy') {
        const cur = periodRange(cmpMode, explicitCmp.current_key);
        mainFrom = cur.from;
        mainTo = cur.to;
    }

    useEffect(() => {
        // Povratak s postavki ponovo ucitava: kljuc cachea na serveru ukljucuje zadnju promjenu postavki.
        if (!token || companyIds.length === 0 || view === 'postavke') return;
        // Odgovor je u dnevnom cacheu na serveru; gumb Osvjezi trazi novi izracun (refresh=1).
        const base: Record<string, string> = { company_ids: companyIds.join(','), ...extra };
        if (refreshTick !== lastTick.current) {
            lastTick.current = refreshTick;
            base.refresh = '1';
        }
        setLoading(true);
        api<KockaPayload>(`/pl-v23?${new URLSearchParams({ ...base, date_from: mainFrom, date_to: mainTo }).toString()}`, token)
            .then((p) => { setPayload(p); setError(''); })
            .catch((e) => setError(e instanceof Error ? e.message : 'P&L kocka se nije ucitala.'))
            .finally(() => setLoading(false));
        if (cmpMode === 'none') {
            setCmpPayload(null);
            return;
        }
        const r = cmpMode === 'custom' ? { from: customCmp.date_from, to: customCmp.date_to } : periodRange(cmpMode, explicitCmp.compare_key);
        api<KockaPayload>(`/pl-v23?${new URLSearchParams({ ...base, date_from: r.from, date_to: r.to }).toString()}`, token)
            .then(setCmpPayload)
            .catch(() => setCmpPayload(null));
    }, [token, companyIds, extra, mainFrom, mainTo, cmpMode, customCmp, explicitCmp, refreshTick, view]);

    // drawer: isti filtri kao pregled, uz vrijednost sloja retka (odgovor ide kroz isti dnevni cache na serveru)
    useEffect(() => {
        setDrawerData(null); setDrawerErr('');
        if (!drawer || !token || companyIds.length === 0) return undefined;
        let dead = false;
        const q = new URLSearchParams({ company_ids: companyIds.join(','), ...extra, layer_value: drawer.key, date_from: mainFrom, date_to: mainTo });
        api<KockaPayload>(`/pl-v23?${q.toString()}`, token)
            .then((p) => { if (!dead) setDrawerData(p); })
            .catch((e) => { if (!dead) setDrawerErr(e instanceof Error ? e.message : 'Detalji se nisu ucitali.'); });
        return () => { dead = true; };
    }, [drawer, token, companyIds, extra, mainFrom, mainTo]);
    // promjena sloja zatvara drawer i vraca zadani redoslijed; promjena filtara samo zatvara drawer
    useEffect(() => { setDrawer(null); setSort(null); }, [layer]);
    useEffect(() => { setDrawer(null); }, [mode, companyIds, mainFrom, mainTo, icMode, alloc, layerValue, view]);
    useEffect(() => {
        const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && drawer) { e.preventDefault(); setDrawer(null); } };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [drawer]);

    const seg = (items: Array<{ key: string; label: string }>, active: string, on: (k: string) => void) => (
        <div className="bi-seg" role="tablist">
            {items.map((m) => (
                <button key={m.key} type="button" className={'bi-seg-btn' + (active === m.key ? ' is-active' : '')} onClick={() => on(m.key)}>{m.label}</button>
            ))}
        </div>
    );

    const toolbarExtra = (
        <>
            <div>
                <label style={lbl}>Nacin prikaza</label>
                {seg(MODES, mode, (k) => setMode(k as Mode))}
            </div>
            <div>
                <label style={lbl}>Sloj</label>
                {seg(LAYERS, layer, (k) => { setLayer(k); setLayerValue(null); })}
            </div>
            <div>
                <label style={lbl}>Alokacija</label>
                {mode === 'knjig'
                    ? <span style={{ fontSize: '0.8rem', color: '#9c8a55' }}>Knjigovodstveno je bez alokacija</span>
                    : seg([{ key: 'prije', label: 'Prije' }, { key: 'poslije', label: 'Poslije' }], alloc ? 'poslije' : 'prije', (k) => setAlloc(k === 'poslije'))}
            </div>
            <div>
                <label style={lbl}>Podaci</label>
                <button type="button" className="bi-seg-btn" disabled={loading} onClick={() => setRefreshTick((t) => t + 1)}
                    title="Ponovno izracunaj iz kocke (nakon novog uvoza)">
                    {loading ? 'Racunam...' : 'Osvjezi'}
                </button>
            </div>
            {layerValue ? (
                <div>
                    <label style={lbl}>Filtar sloja</label>
                    <button type="button" className="bi-seg-btn is-active" onClick={() => setLayerValue(null)} title="Ukloni filtar">
                        {layerValue.label} x
                    </button>
                </div>
            ) : null}
        </>
    );

    const rawRows = payload?.layer?.rows ?? [];
    const rows = useMemo(() => {
        if (!sort) return rawRows;
        const { col, dir } = sort;
        return [...rawRows].sort((a, b) => (col === 'label' ? dir * a.label.localeCompare(b.label, 'hr') : dir * ((a[col] as number) - (b[col] as number))));
    }, [rawRows, sort]);
    const th: React.CSSProperties = { textAlign: 'right', padding: '0.4rem 0.6rem', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9c8a55' };
    const td: React.CSSProperties = { textAlign: 'right', padding: '0.4rem 0.6rem', fontVariantNumeric: 'tabular-nums' };
    const sh = (col: SortKey, label: string, left = false) => (
        <th style={{ ...th, textAlign: left ? 'left' : 'right', cursor: 'pointer', userSelect: 'none' }}
            aria-sort={sort?.col === col ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
            onClick={() => setSort((s) => (s && s.col === col ? { col, dir: (s.dir === 1 ? -1 : 1) as 1 | -1 } : { col, dir: col === 'label' ? 1 : -1 }))}
            title="Klik: sortiraj po ovom stupcu (ponovni klik mijenja smjer)">
            {label}{sort?.col === col ? (sort.dir === 1 ? ' ^' : ' v') : ''}
        </th>
    );
    const drawerPanel = drawer ? (() => {
        const d = drawerData;
        const cols = d?.columns ?? [];
        const subs = d?.sub_buckets ?? [];
        const dtd: React.CSSProperties = { ...td, padding: '0.25rem 0.45rem', fontSize: '0.78rem', whiteSpace: 'nowrap' };
        const dth: React.CSSProperties = { ...th, padding: '0.25rem 0.45rem', fontSize: '0.66rem', whiteSpace: 'nowrap' };
        const kv: Array<[string, string]> = [
            ['Prihodi', formatMoney(drawer.revenue, locale)],
            ['COGS', formatMoney(drawer.cogs, locale)],
            ['Bruto marza', `${formatMoney(drawer.gross, locale)} (${pct(drawer.gross, drawer.revenue)})`],
            ['OPEX', formatMoney(drawer.opex, locale)],
            ['EBITDA', `${formatMoney(drawer.ebitda, locale)} (${pct(drawer.ebitda, drawer.revenue)})`],
            ['Rezultat', formatMoney(drawer.net, locale)],
            ['Redaka kocke', drawer.count.toLocaleString('hr-HR')],
        ];
        return (
            <aside style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(640px, 48vw)', zIndex: 4100, background: '#fff', borderLeft: '2px solid #1a202c', overflowY: 'auto', padding: '14px 18px 28px', boxShadow: 'none' }}
                aria-label={`Detalji: ${drawer.label}`}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ marginRight: 'auto' }}>
                        <div style={lbl}>{payload?.layer?.label}</div>
                        <h3 style={{ margin: 0, fontSize: 16 }}>{drawer.label}</h3>
                    </div>
                    <button type="button" className="bi-seg-btn" onClick={() => { setLayerValue({ key: drawer.key, label: drawer.label }); setDrawer(null); }}>Filtriraj pregled</button>
                    <button type="button" className="bi-seg-btn" onClick={() => setDrawer(null)} title="Esc">Zatvori</button>
                </div>
                {drawer.alloc_warning ? <div style={{ marginTop: 8, fontSize: '0.8rem', color: '#c62828' }}>! {drawer.alloc_warning}</div> : null}
                <table style={{ width: '100%', minWidth: 0, borderCollapse: 'collapse', marginTop: 10 }}><tbody>
                    {kv.map(([k, v]) => <tr key={k} style={{ borderTop: '1px solid rgba(156,138,85,0.2)' }}><td style={{ ...dtd, textAlign: 'left', color: '#555' }}>{k}</td><td style={dtd}>{v}</td></tr>)}
                </tbody></table>
                {drawerErr ? <div style={{ color: '#c62828', marginTop: 10 }}>{drawerErr}</div> : null}
                {!d && !drawerErr ? <div style={{ marginTop: 12, color: '#777' }}>Ucitavam detalje...</div> : null}
                {d ? (
                    <>
                        <div style={{ ...lbl, marginTop: 16 }}>Po mjesecima</div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 0 }}>
                                <thead><tr><th style={{ ...dth, textAlign: 'left' }}>Kategorija</th>{cols.map((c) => <th key={c.key} style={dth}>{c.label}</th>)}<th style={dth}>Ukupno</th></tr></thead>
                                <tbody>
                                    {d.buckets.map((b) => (
                                        <tr key={b.bucket_lvl1} style={{ borderTop: '1px solid rgba(156,138,85,0.2)' }}>
                                            <td style={{ ...dtd, textAlign: 'left' }}>{b.bucket_lvl1}</td>
                                            {cols.map((c) => <td key={c.key} style={dtd}>{formatMoney(b.by_period?.[c.key] ?? 0, locale)}</td>)}
                                            <td style={{ ...dtd, fontWeight: 700 }}>{formatMoney(b.total_eur, locale)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ ...lbl, marginTop: 16 }}>Najvece stavke po kategoriji</div>
                        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 0 }}><tbody>
                            {d.buckets.map((b) => {
                                const top = subs.filter((x) => x.bucket_lvl1 === b.bucket_lvl1).slice(0, 6);
                                return top.length === 0 ? null : (
                                    <React.Fragment key={b.bucket_lvl1}>
                                        <tr><td colSpan={3} style={{ ...dtd, textAlign: 'left', fontWeight: 700, paddingTop: '0.6rem' }}>{b.bucket_lvl1}</td></tr>
                                        {top.map((x) => (
                                            <tr key={x.bucket_lvl2} style={{ borderTop: '1px solid rgba(156,138,85,0.2)' }}>
                                                <td style={{ ...dtd, textAlign: 'left', whiteSpace: 'normal' }}>{x.bucket_lvl2}</td>
                                                <td style={{ ...dtd, color: '#777' }}>{x.count.toLocaleString('hr-HR')}</td>
                                                <td style={dtd}>{formatMoney(x.total_eur, locale)}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody></table>
                    </>
                ) : null}
            </aside>
        );
    })() : null;
    const belowKpi = rows.length > 0 ? (
        <div className="card" style={{ padding: '1rem' }}>
            <h3 style={{ margin: '0 0 0.65rem 0' }}>Po sloju: {payload?.layer?.label}{layerValue ? ` (filtar: ${layerValue.label})` : ''}</h3>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            {sh('label', payload?.layer?.label ?? '', true)}
                            {sh('revenue', 'Prihodi')}{sh('cogs', 'COGS')}{sh('gross', 'Bruto marza')}{sh('opex', 'OPEX')}{sh('ebitda', 'EBITDA')}{sh('net', 'Rezultat')}
                            <th style={th} aria-label="Filtar" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.key} tabIndex={0} style={{ cursor: 'pointer', borderTop: '1px solid rgba(156,138,85,0.2)', background: drawer?.key === r.key ? 'rgba(26,32,44,0.12)' : layerValue?.key === r.key ? 'rgba(244,190,32,0.28)' : undefined }}
                                onClick={() => setDrawer(drawer?.key === r.key ? null : r)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDrawer(drawer?.key === r.key ? null : r); } }}
                                title="Klik: detalji u draweru; gumb Filtar: filtriraj cijeli pregled na ovu vrijednost">
                                <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>
                                    {r.alloc_warning ? <span style={{ color: '#c62828', fontWeight: 800, marginRight: 6 }} title={r.alloc_warning}>!</span> : null}
                                    {r.label}
                                </td>
                                <td style={td}>{formatMoney(r.revenue, locale)}</td>
                                <td style={td}>{formatMoney(r.cogs, locale)}</td>
                                <td style={{ ...td, color: r.gross < 0 ? '#c62828' : undefined }}>{formatMoney(r.gross, locale)}</td>
                                <td style={td}>{formatMoney(r.opex, locale)}</td>
                                <td style={{ ...td, fontWeight: 700 }}>{formatMoney(r.ebitda, locale)}</td>
                                <td style={td}>{formatMoney(r.net, locale)}</td>
                                <td style={{ ...td, width: '1%' }}>
                                    <button type="button" className={'bi-seg-btn' + (layerValue?.key === r.key ? ' is-active' : '')} style={{ padding: '2px 8px', fontSize: '0.72rem' }}
                                        onClick={(e) => { e.stopPropagation(); setLayerValue(layerValue?.key === r.key ? null : { key: r.key, label: r.label }); }}>
                                        {layerValue?.key === r.key ? 'Makni filtar' : 'Filtar'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9c8a55', marginTop: '0.5rem' }}>
                Izvor: kocka bi_pl_facts_v2, nacin {MODES.find((m) => m.key === mode)?.label}{payload?.built_at ? `, izgradena ${payload.built_at}` : ''}
                {payload?.cache ? `; izracun ${payload.cache.computed_at.slice(0, 16).replace('T', ' ')}${payload.cache.hit ? ' (iz cachea)' : ''}` : ''}.
                {payload?.allocation?.applied ? ' Overhead je rasporeden na sektore po ekonomskom RUC-u (negativan RUC = 0, oznaka !); plaće po osobi jos nisu u alokaciji.' : ''}
                {payload?.allocation?.note ? ` ${payload.allocation.note}` : ''}
                {payload?.allocation && Object.keys(payload.allocation.unallocated).length > 0
                    ? ` Nerasporedeno (nema pozitivnog RUC-a): ${Object.entries(payload.allocation.unallocated).map(([m, v]) => `${m} ${formatMoney(v, locale)}`).join(', ')}.` : ''}
            </div>
        </div>
    ) : null;

    const toggleCompany = (id: number) => {
        const next = companyIds.includes(id) ? companyIds.filter((c) => c !== id) : [...companyIds, id];
        if (next.length > 0) onFiltersChange?.({ companyIds: next });
    };
    const rangeLocked = cmpMode === 'mom' || cmpMode === 'qoq' || cmpMode === 'yoy';
    const btn: React.CSSProperties = { padding: '6px 14px', border: '1px solid #1a202c', background: '#1a202c', color: '#fff', fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', cursor: 'pointer' };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 4000, background: '#fff', display: 'flex', flexDirection: 'column' }}>
            <header style={{ borderBottom: '2px solid #1a202c', padding: '10px 24px', display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginRight: 'auto' }}>
                    <div style={{ padding: '4px 10px', background: '#1a202c', color: '#fff', fontWeight: 700, fontSize: 12, letterSpacing: '0.06em' }}>MODUL 2.3</div>
                    <h1 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>P&amp;L KOCKA</h1>
                </div>
                {companies.length > 0 ? (
                    <div>
                        <label style={lbl}>Tvrtke</label>
                        <div className="bi-seg">
                            {companies.map((c) => (
                                <button key={c.id} type="button" className={'bi-seg-btn' + (companyIds.includes(c.id) ? ' is-active' : '')} onClick={() => toggleCompany(c.id)}>{c.code}</button>
                            ))}
                        </div>
                    </div>
                ) : null}
                <div title={rangeLocked ? 'Uz usporedbu MoM/QoQ/YoY razdoblje odreduje izbor usporedbe.' : undefined} style={{ opacity: rangeLocked ? 0.45 : 1 }}>
                    <label style={lbl}>Razdoblje</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <DateInput value={dateFrom} onChange={(v) => setRange({ from: v, to: v > dateTo ? v : dateTo })} style={{ width: 120 }} />
                        <span>-</span>
                        <DateInput value={dateTo} onChange={(v) => setRange({ from: v < dateFrom ? v : dateFrom, to: v })} style={{ width: 120 }} />
                    </div>
                </div>
                <button type="button" style={{ ...btn, background: rebuilding ? '#7f1d1d' : '#c62828', borderColor: '#7f1d1d' }} disabled={rebuilding}
                    onClick={rebuildCache} title="Obrisi dnevni cache i preracunaj sve prikaze">{rebuilding ? 'REBUILD...' : 'REBUILD CACHE'}</button>
                <button type="button" style={{ ...btn, background: view === 'postavke' ? '#fff' : '#1a202c', color: view === 'postavke' ? '#1a202c' : '#fff' }}
                    onClick={() => setView(view === 'postavke' ? 'pnl' : 'postavke')}>{view === 'postavke' ? 'NATRAG NA P&L' : 'POSTAVKE'}</button>
                {onExit ? <button type="button" style={btn} onClick={onExit}>ZATVORI</button> : null}
            </header>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
            {view === 'postavke' ? <PnlKockaSettings token={token} /> : (<>
            {error ? <div className="card" style={{ color: '#c62828' }}>{error}</div> : null}
            <PnlV2Module
                payload={payload}
                comparePayload={cmpPayload}
                loading={loading}
                locale={locale}
                intercompanyMode={icMode}
                onIntercompanyModeChange={setIcMode}
                comparisonMode={cmpMode}
                onComparisonModeChange={setCmpMode}
                customCompare={customCmp}
                onCustomCompareChange={setCustomCmp}
                explicitCompare={explicitCmp}
                onExplicitCompareChange={setExplicitCmp}
                token={token}
                companyIds={companyIds}
                dateFrom={mainFrom}
                dateTo={mainTo}
                drillPath="/pl-v23/drilldown"
                drillExtra={extra}
                toolbarExtra={toolbarExtra}
                belowKpi={belowKpi}
            />
            </>)}
            </div>
            {drawerPanel}
        </div>
    );
}
